import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ThreadListResponse } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { formatDateTime } from '../../lib/date';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';

/** A single thread's messages + a reply box. */
function ThreadPanel({ threadId }: { threadId: string }) {
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const thread = useQuery({
    queryKey: ['portal', 'thread', threadId],
    queryFn: () => api.messaging.getThread(threadId),
  });

  const post = useMutation({
    mutationFn: () => api.messaging.postMessage(threadId, { body }),
    onSuccess: () => {
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['portal', 'thread', threadId] });
      void queryClient.invalidateQueries({ queryKey: ['portal', 'messages'] });
      void queryClient.invalidateQueries({ queryKey: ['portal', 'overview'] });
    },
  });

  if (thread.isLoading) return <Loading label="Loading conversation…" />;
  if (thread.isError || !thread.data)
    return <ErrorState message={errorMessage(thread.error)} onRetry={() => thread.refetch()} />;

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>{thread.data.thread.subject}</h3>
      <ol style={{ listStyle: 'none', padding: 0, margin: 0 }} className="stack">
        {thread.data.messages.map((m) => (
          <li key={m.id} style={{ borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem' }}>
            <p style={{ margin: 0 }}>{m.body}</p>
            <p className="muted" style={{ margin: '0.15rem 0 0', fontSize: '0.8rem' }}>
              {formatDateTime(m.createdAt)}
            </p>
          </li>
        ))}
      </ol>
      <form
        className="stack"
        style={{ marginTop: '1rem' }}
        onSubmit={(e) => {
          e.preventDefault();
          if (body.trim()) post.mutate();
        }}
      >
        {post.isError && <StatusMessage tone="err">{errorMessage(post.error)}</StatusMessage>}
        <div>
          <label htmlFor="reply-body">Your message</label>
          <textarea
            id="reply-body"
            rows={3}
            value={body}
            maxLength={8000}
            onChange={(e) => setBody(e.target.value)}
          />
        </div>
        <div>
          <button type="submit" disabled={post.isPending || !body.trim()}>
            {post.isPending ? 'Sending…' : 'Send message'}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Start a NEW conversation with the property manager. Sends a DIRECT thread (no lease/unit) — the
 * server routes it to the org's active owner/staff — so a tenant WITHOUT an active lease can still
 * reach management (their message can never fall into a void). On success we select the fresh
 * thread so the reply box is immediately in view.
 */
function NewThreadForm({ onCreated }: { onCreated: (id: string) => void }) {
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const create = useMutation({
    mutationFn: () => api.messaging.createThread({ subject: subject.trim(), body: body.trim() }),
    onSuccess: (res) => {
      setSubject('');
      setBody('');
      void queryClient.invalidateQueries({ queryKey: ['portal', 'messages'] });
      void queryClient.invalidateQueries({ queryKey: ['portal', 'overview'] });
      onCreated(res.thread.id);
    },
  });

  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        if (subject.trim() && body.trim()) create.mutate();
      }}
    >
      <h3 style={{ marginTop: 0 }}>New message to your property manager</h3>
      {create.isError && <StatusMessage tone="err">{errorMessage(create.error)}</StatusMessage>}
      <div>
        <label htmlFor="new-subject">Subject</label>
        <input
          id="new-subject"
          type="text"
          value={subject}
          maxLength={200}
          placeholder="What's this about?"
          onChange={(e) => setSubject(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor="new-body">Message</label>
        <textarea
          id="new-body"
          rows={4}
          value={body}
          maxLength={8000}
          placeholder="Write your message…"
          onChange={(e) => setBody(e.target.value)}
        />
      </div>
      <div>
        <button type="submit" disabled={create.isPending || !subject.trim() || !body.trim()}>
          {create.isPending ? 'Sending…' : 'Send message'}
        </button>
      </div>
    </form>
  );
}

export function MessagesView({ data }: { data: ThreadListResponse }) {
  const [selected, setSelected] = useState<string | null>(data.threads[0]?.id ?? null);
  const [composing, setComposing] = useState(false);

  const onCreated = (id: string) => {
    setSelected(id);
    setComposing(false);
  };

  return (
    <>
      <h1>Messages</h1>
      <p className="subtitle">Communicate with your property manager.</p>

      {data.threads.length === 0 ? (
        <>
          <p className="muted">
            You have no conversations yet. Send a message below and your property manager will get
            back to you.
          </p>
          <NewThreadForm onCreated={onCreated} />
        </>
      ) : (
        <>
          <div className="row" style={{ marginBottom: '1rem' }}>
            <button
              type="button"
              className={composing ? 'secondary' : ''}
              aria-expanded={composing}
              onClick={() => setComposing((v) => !v)}
            >
              {composing ? 'Cancel' : 'New message'}
            </button>
          </div>
          {composing && (
            <div style={{ marginBottom: '1rem' }}>
              <NewThreadForm onCreated={onCreated} />
            </div>
          )}
          <div className="grid" style={{ gridTemplateColumns: 'minmax(200px, 1fr) 2fr' }}>
            <nav aria-label="Conversations">
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} className="stack">
                {data.threads.map((t) => (
                  <li key={t.id}>
                    <button
                      type="button"
                      className={selected === t.id ? '' : 'secondary'}
                      style={{ width: '100%', textAlign: 'left' }}
                      aria-current={selected === t.id ? 'true' : undefined}
                      onClick={() => setSelected(t.id)}
                    >
                      {t.subject}
                      {t.unreadCount ? ` (${t.unreadCount} unread)` : ''}
                    </button>
                  </li>
                ))}
              </ul>
            </nav>
            <section aria-labelledby="conversation-heading">
              {/* h2 keeps the outline h1→h2→h3 (the thread subject below is an h3) — same visual
                  weight as before via `.visually-hidden` (WCAG 1.3.1, no skipped heading level). */}
              <h2 id="conversation-heading" className="visually-hidden">
                Conversation
              </h2>
              {selected && <ThreadPanel threadId={selected} />}
            </section>
          </div>
        </>
      )}
    </>
  );
}

export function Messages() {
  const query = useQuery({
    queryKey: ['portal', 'messages'],
    queryFn: () => api.portal.messages(),
  });
  if (query.isLoading) return <Loading label="Loading your messages…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;
  return <MessagesView data={query.data} />;
}
